import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Plus, Ban, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import IssuerLayout from "@/components/IssuerLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewCertificates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      }
      
      const token = localStorage.getItem('issuer_token');
      const skip = loadMore ? recipients.length : 0;
      
      const [recipientsRes, certsRes] = await Promise.all([
        axios.get(`${API}/recipients?skip=${skip}&limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        loadMore ? Promise.resolve({ data: { items: certificates } }) : axios.get(`${API}/certificates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Handle paginated responses
      const recipientData = recipientsRes.data.items || recipientsRes.data;
      const certificateData = certsRes.data.items || certsRes.data;
      
      if (loadMore) {
        setRecipients(prev => [...prev, ...recipientData]);
      } else {
        setRecipients(recipientData);
        setCertificates(certificateData);
      }
      
      setHasMore(recipientsRes.data.has_more || false);
      setTotal(recipientsRes.data.total || recipientData.length);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleRevoke = async (recipientId) => {
    if (!confirm("Are you sure you want to revoke this certificate?")) return;

    try {
      const token = localStorage.getItem('issuer_token');
      await axios.post(`${API}/recipients/${recipientId}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Certificate revoked");
      fetchData();
    } catch (err) {
      toast.error("Failed to revoke certificate");
    }
  };

  const getCertificateName = (certId) => {
    const cert = certificates.find(c => c.id === certId);
    return cert ? cert.title : "Unknown";
  };

  const filteredRecipients = recipients.filter(r => 
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.serial_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <IssuerLayout>
        <div className="flex items-center justify-center h-64" data-testid="loading-certificates">
          <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
        </div>
      </IssuerLayout>
    );
  }

  return (
    <IssuerLayout>
      <div data-testid="view-certificates-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Issued Certificates</h1>
            <p className="text-slate-600">{filteredRecipients.length} certificates issued</p>
          </div>
          <Button 
            onClick={() => navigate("/issuer/create")}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3"
            data-testid="create-new-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or serial number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 rounded-lg border-slate-200"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {filteredRecipients.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-certificates">
              <p className="text-slate-500">No certificates issued yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="certificates-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Recipient</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Certificate</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Serial</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredRecipients.map((recipient, idx) => (
                    <tr key={recipient.id} className="hover:bg-slate-50" data-testid={`recipient-row-${idx}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{recipient.name}</p>
                          <p className="text-xs text-slate-500">{recipient.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{getCertificateName(recipient.certificate_id)}</td>
                      <td className="px-6 py-4">
                        <code className="text-xs text-slate-900 font-mono bg-slate-100 px-2 py-1 rounded">{recipient.serial_number}</code>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{recipient.completion_date}</td>
                      <td className="px-6 py-4">
                        {recipient.revoked ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`status-revoked-${idx}`}>
                            Revoked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid={`status-active-${idx}`}>
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${BACKEND_URL}/verify/${recipient.serial_number}`, '_blank')}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`view-btn-${idx}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          {!recipient.revoked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevoke(recipient.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`revoke-btn-${idx}`}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Pagination Info & Load More */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {filteredRecipients.length} of {total} certificates
          </p>
          {hasMore && !searchTerm && (
            <Button
              onClick={() => fetchData(true)}
              disabled={loadingMore}
              variant="outline"
              className="border-slate-200"
              data-testid="load-more-btn"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                `Load More (${total - recipients.length} remaining)`
              )}
            </Button>
          )}
        </div>
      </div>
    </IssuerLayout>
  );
};

export default ViewCertificates;
